import { useAsync } from 'react-async';
import { isRetryableError } from '@koob/utils';
import {
  KoobContainer,
  KoobTooltip,
  KoobButton,
  KoobInputStars,
  KoobAccordionGroup,
  KoobAccordion,
  PromotionIncludedPrice,
  PromotionTag,
} from '@koob/ui';
import {
  HotelTag,
  LigneHorizontal,
  SubSectionTitle,
  SubSectionTitleContainer,
} from 'components/Hotel/HotelDetails/Base';
import { getHotel, getHotelsAvailability } from '../../api/hotels';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BlocRight from 'components/Hotel/HotelDetails/BlocRight';
import AssistantBlock from 'components/Assistant/AssistantBlock';
import { useTranslation } from 'react-i18next';
import ServiceAndFacilities from 'components/Hotel/HotelDetails/ServiceAndFacilities';
import SustainableCommitments from 'components/Hotel/HotelDetails/SustainableCommitments';
import RestaurantsHotel from 'components/Hotel/HotelDetails/RestaurantsHotel';
import styled from 'styled-components';
import { Spinner } from '@koob/margaret';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useBooking, useError, useLanguage, useSnack } from '../../hooks';
import { isEmpty, omit } from 'lodash';
import MeetingRooms from 'components/Hotel/HotelDetails/MeetingRooms';
import KoobDescriptionComponent from 'components/Koob/KoobDescriptionComponent';
import KoobLocationComponent from 'components/Koob/KoobLocationComponent';
import KoobHeader from 'components/Koob/KoobHeader';
import { SectionCard } from 'components/Fields';
import { formatCurrency } from '../../utils';
import { dayjs, daysBetween, formatDate, parseDateOnly } from '@koob/dates';
import { RoleButton as Button } from '../../components/RoleButton';
import { requestConnection } from '../../api/requestConnection';
import { NotFoundContent } from '../NotFound';
import { BlocImages } from 'components/Hotel/HotelDetails/BlocImages';
import KoobPlaceholder from '../../components/Koob/KoobPlaceholder';
import BookingRoomsCompositionModal from 'components/Hotel/BookingRoomsCompositionModal';
import HotelGetAvailabilityModal from '../../components/Hotel/HotelGetAvailabilityModal';
import BookingSummaryCard from '../../components/BookingSummaryCard';
import { toHotelSearchRooms } from '../../components/GuestsRoomsPicker';
import { useExperienceBooking } from '../../hooks';
import { ApiPollingStateEnum, SustainableLevelEnum } from '@koob/enums';

export const SpinnerContainer = styled.div`
  width: 100%;
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

export default function HotelsDetail() {
  const { hotelId } = useParams();
  const { t } = useTranslation('hotel');
  const { filters, setFilters, setCustomerFile, replaceBookingId } =
    useBooking();
  const initialListFiltersRef = useRef(filters);
  const { lang, setLang } = useLanguage();
  const { notify } = useSnack();
  const { sendErrorSnack } = useError();
  const navigate = useNavigate();
  const urlLocale = new URLSearchParams(window.location.search).get('locale');
  // A shared link carries its language; adopt it for the session so the rooms
  // and booking tunnel follow the same locale as the hotel presentation.
  useEffect(() => {
    if (urlLocale) setLang(urlLocale);
  }, [urlLocale, setLang]);

  const {
    data,
    reload,
    error: hotelError,
  } = useAsync({
    promiseFn: getHotel,
    hotelId,
    locale: urlLocale || lang,
    watchFn: (props, prevProps) => props.locale !== prevProps.locale,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [showRoomCompositionModal, setShowRoomCompositionModal] =
    useState(undefined);
  const [showGetAvailabilityModal, setShowGetAvailabilityModal] =
    useState(false);
  const [basketAvailable, setBasketAvailable] = useState(true);

  const updateFilterValue = useCallback(
    (property, value) => {
      setFilters(current => ({
        ...current,
        [property]: value,
      }));
    },
    [setFilters],
  );

  const requestFilters = {
    hotels: [hotelId],
    rooms: filters?.rooms?.map(room => ({
      ...omit(room, 'children'),
      firstAdultNationality: room?.firstAdultNationality?.value,
      count: 1,
    })),
    startAt: filters?.startAt,
    endAt: filters?.endAt,
  };

  const filtersKey =
    filters?.startAt && filters?.endAt && filters?.rooms?.length > 0
      ? JSON.stringify(requestFilters)
      : null;

  const totalPax = filters?.rooms?.reduce(
    (acc, room) => acc + room.adults + room.childrenBirthdates?.length ?? 0,
    0,
  );

  const { data: priceData, reload: reloadPrice } = useAsync({
    promiseFn: getHotelsAvailability,
    filters: requestFilters,
    locale: urlLocale || lang,
    filtersKey,
    watchFn: (props, prevProps) =>
      props.locale !== prevProps.locale ||
      (props.filtersKey !== prevProps.filtersKey && props.filtersKey !== null),
    onResolve: results => {
      if (results?.data?.state === ApiPollingStateEnum.IN_PROGRESS) {
        setTimeout(() => {
          reloadPrice();
        }, 3000);
      }
    },
    onReject: error => {
      // A 4xx will fail identically on every replay — retrying it just spins.
      if (!isRetryableError(error)) return;
      setTimeout(() => {
        reloadPrice();
      }, 5000);
    },
  });

  const hotelPriceObj =
    priceData?.data?.data?.length > 0 ? priceData.data.data[0] : undefined;
  const hotel = data?.data;
  const minimumStay = hotelPriceObj?.minimumStay;
  let canReload =
    filters?.experienceId &&
    minimumStay === undefined &&
    !isEmpty(hotelPriceObj);
  const canDisplay =
    filters?.experienceId &&
    !canReload &&
    isEmpty(hotelPriceObj) &&
    Boolean(priceData?.data);
  const { addExperienceToBasket } = useExperienceBooking();

  useEffect(() => {
    const programBookingFilters = JSON.parse(
      localStorage.getItem('programBookingInfos'),
    );
    const isFromProgram =
      !isEmpty(programBookingFilters?.experienceComposition) &&
      !isEmpty(programBookingFilters?.startAt) &&
      !isEmpty(programBookingFilters?.endAt);
    const startDate = programBookingFilters?.startAt
      ? parseDateOnly(programBookingFilters?.startAt)
      : null;
    const programDate = startDate
      ? formatDate(
          dayjs(startDate).add(programBookingFilters?.day, 'day'),
          'YYYY-MM-DD',
        )
      : null;

    if (isFromProgram) {
      setFilters(current => ({
        ...current,
        rooms: programBookingFilters?.experienceComposition,
        experienceId: programBookingFilters?.experienceId,
        programDay: programBookingFilters?.day,
        startAt: programDate,
        endAt: formatDate(dayjs(programDate).add(1, 'day'), 'YYYY-MM-DD'),
        minDate: programBookingFilters?.startAt,
        maxDate: programBookingFilters?.endAt,
      }));
      if (programBookingFilters?.clientFolder) {
        setCustomerFile(programBookingFilters.clientFolder);
      }
      localStorage.removeItem('programBookingInfos');
      setShowRoomCompositionModal(true);
    }
  }, []);

  useEffect(() => {
    if (hotel) {
      setIsLoading(false);
    }
  });

  const cancelationPoliciesObj =
    hotel?.cancelationPolicies && hotel?.cancelationPolicies.length > 0
      ? {
          content: hotel?.cancelationPolicies,
          assistantSection: 'Contracting Conditions',
          description: {
            displayName: t('contractingPolicies'),
          },
        }
      : undefined;

  const mainDescription = hotel?.hotelDescriptions?.find(
    elem => elem.content && elem.description.isMainDescription,
  );

  let accordionRows =
    hotel?.hotelDescriptions?.filter(
      elem => elem.content && !elem.description.isMainDescription,
    ) ?? [];

  if (cancelationPoliciesObj) {
    accordionRows = [cancelationPoliciesObj, ...accordionRows];
  }

  const hotelImages = [];

  hotelImages.push(hotel?.primaryAttachment);
  if (hotel?.secondaryAttachments?.length > 0) {
    hotelImages.push(...hotel.secondaryAttachments);
  }

  (hotel?.restaurants ?? []).forEach(restau => {
    restau?.attachments.forEach(attach => {
      hotelImages.push(attach);
    });
  });

  (hotel?.meetingRooms ?? []).forEach(room => {
    room?.attachments.forEach(attach => {
      hotelImages.push(attach);
    });
  });

  const hasFilter =
    filters?.location &&
    filters?.startAt &&
    filters?.endAt &&
    filters?.rooms &&
    filters?.rooms?.length > 0;

  const noAvailabilityAfterSearch =
    hasFilter && priceData?.data?.state === 'finished' && !hotelPriceObj;

  const isSearchInProgress =
    hasFilter &&
    (priceData === undefined ||
      priceData?.data?.state === ApiPollingStateEnum.IN_PROGRESS);

  const nbNights = daysBetween(filters?.startAt, filters?.endAt);

  const searchAvailability = ({ rooms, startAt, endAt }) =>
    setFilters(current => ({
      ...current,
      location: { kind: 'hotel', id: hotelId },
      rooms,
      startAt,
      endAt,
    }));

  const handleRequest = async () => {
    try {
      await requestConnection(hotel?.organization?.organizationId, 'hotel');
      reload();
      notify(t('requestConnectionSuccess'));
    } catch (error) {
      sendErrorSnack(error);
    }
  };

  const handleBookingStart = () => {
    navigate('booking/');
  };

  const handleAddToBasket = () => {
    addExperienceToBasket(
      {
        ...hotel,
        durationDays:
          filters?.startDate && filters?.endDate
            ? daysBetween(filters?.startDate, filters?.endDate)
            : 0,
        type: 'hotel',
        name: hotelPriceObj?.displayName,
        selectedExtras: [],
        imageUrl: hotelPriceObj?.imageUrl,
        hotelId: hotel?.id,
        price: {
          promotions: [],
        },
        hotelPricing: {
          price: hotelPriceObj?.price,
          currency: hotelPriceObj?.currency,
          rooms: hotelPriceObj?.rooms,
        },
      },
      {
        startAt: filters?.startAt,
        endAt: filters?.endAt,
        experienceComposition: filters?.rooms,
        hotel: [{ id: hotel?.id }],
      },
    );
    notify(t('addedToBasket'));
  };

  const urlParams = new URLSearchParams(window.location.search);
  const source = urlParams.get('source');

  // A TO without a connection to the hotel's DMC gets a 404 (KD-3873).
  if ([403, 404].includes(hotelError?.response?.status)) {
    return <NotFoundContent />;
  }

  return (
    <>
      <KoobContainer>
        {isLoading && (
          <SpinnerContainer>
            <Spinner />
          </SpinnerContainer>
        )}

        {hotel && (
          <div className="max-w-5xl mx-auto">
            {!replaceBookingId && (
              <div className="flex items-center space-x-1 mb-3">
                <div>
                  {!source && (
                    <Link
                      to="/hotels"
                      onClick={() => setFilters(initialListFiltersRef.current)}
                      className="text-gray-500 font-medium hover:text-orange-500 transition flex items-center space-x-1"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 448 512">
                        <path d="M7.4 273.4C2.7 268.8 0 262.6 0 256s2.7-12.8 7.4-17.4l176-168c9.6-9.2 24.8-8.8 33.9 .8s8.8 24.8-.8 33.9L83.9 232 424 232c13.3 0 24 10.7 24 24s-10.7 24-24 24L83.9 280 216.6 406.6c9.6 9.2 9.9 24.3 .8 33.9s-24.3 9.9-33.9 .8l-176-168z" />
                      </svg>
                      <div className="text-lg">{t('backToCatalog')}</div>
                    </Link>
                  )}
                </div>

                {!isEmpty(hotelPriceObj) && !source && (
                  <KoobTooltip
                    tip={t('hotelBookingInfo')}
                    color="purple"
                    hasArrow={false}
                    position="right-bottom"
                    maxWidth={512}
                  >
                    <svg className="h-5 w-5 no-hover" viewBox="0 0 512 512">
                      <path d="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336c-13.3 0-24 10.7-24 24s10.7 24 24 24h80c13.3 0 24-10.7 24-24s-10.7-24-24-24h-8V248c0-13.3-10.7-24-24-24H216c-13.3 0-24 10.7-24 24s10.7 24 24 24h24v64H216zm40-144a32 32 0 1 0 0-64 32 32 0 1 0 0 64z" />
                    </svg>
                  </KoobTooltip>
                )}
              </div>
            )}

            <BlocImages images={hotelImages} />

            <div className="grid md:grid-cols-3 gap-5">
              <div className="relative w-full col-span-2">
                <div className="w-full">
                  <div data-assistant-section="General information">
                    <KoobHeader
                      title={hotel?.displayName}
                      tagGroups={[
                        {
                          label: t('hotelCategory'),
                          tags: (hotel?.categories ?? []).map(
                            elem => elem.displayName,
                          ),
                        },
                        {
                          label: t('hotelTypology'),
                          tags: (hotel?.kinds ?? []).map(
                            elem => elem.displayName,
                          ),
                        },
                      ]}
                    >
                      <KoobInputStars value={hotel?.stars} />
                      <div className="flex space-x-1.5 justify-end items-center">
                        <div className="flex items-center space-x-1">
                          {[
                            ...Array(
                              SustainableLevelEnum[
                                hotel?.sustainable?.level?.toUpperCase()
                              ],
                            ),
                          ].map((_, i) => (
                            <i
                              key={i}
                              className="fa fa-regular fa-leaf text-green-600 text-xl"
                            />
                          ))}
                        </div>
                      </div>
                    </KoobHeader>
                  </div>

                  <AssistantBlock
                    productType="hotel"
                    productId={hotelId}
                    productName={hotel?.displayName}
                    context={source === 'trip' ? 'trip' : 'marketplace'}
                    locale={urlLocale || lang}
                    onSearchAvailability={
                      source
                        ? undefined
                        : ({ composition, startAt, endAt }) =>
                            searchAvailability({
                              rooms: toHotelSearchRooms(composition),
                              startAt,
                              endAt,
                            })
                    }
                  />

                  <div data-assistant-section="Description">
                    <KoobDescriptionComponent
                      title={t('description')}
                      description={mainDescription?.content}
                    />
                  </div>

                  {(accordionRows.length > 0 ||
                    (hotel?.styles ?? []).length > 0) && (
                    <>
                      <LigneHorizontal />
                      <SubSectionTitleContainer>
                        <div className="flex flex-wrap items-center pb-4">
                          <div className="flex-1">
                            <SubSectionTitle>{t('hotelStyle')}</SubSectionTitle>
                          </div>
                          {(hotel?.styles ?? []).map((style, index) => (
                            <div className="py-1 mr-2 flex-none" key={index}>
                              <HotelTag color="orange">
                                <div className="text-xs font-extrabold uppercase leading-none m-0">
                                  {style.displayName}
                                </div>
                              </HotelTag>
                            </div>
                          ))}
                        </div>

                        <KoobAccordionGroup>
                          {accordionRows.map((row, index) => (
                            <KoobAccordion
                              key={index}
                              data-assistant-section={
                                row.assistantSection ??
                                row.description?.displayName
                              }
                              title={row.description?.displayName}
                            >
                              {row.content}
                            </KoobAccordion>
                          ))}
                        </KoobAccordionGroup>

                        <LigneHorizontal />
                      </SubSectionTitleContainer>
                    </>
                  )}

                  <div data-assistant-section="Location">
                    <KoobLocationComponent
                      markers={[
                        {
                          lat: '' + hotel?.lat,
                          lon: '' + hotel?.lon,
                        },
                      ]}
                      title={t('location')}
                      address={hotel.address}
                      tags={(hotel?.locations ?? []).map(
                        elem => elem.displayName,
                      )}
                    />
                  </div>

                  <div data-assistant-section="Services & facilities">
                    <ServiceAndFacilities hotel={hotel} />
                  </div>

                  <div data-assistant-section="Sustainable commitments">
                    <SustainableCommitments hotel={hotel} />
                  </div>

                  <div data-assistant-section="Restaurants">
                    <RestaurantsHotel hotel={hotel} />
                  </div>

                  <div data-assistant-section="Meeting Rooms">
                    <MeetingRooms hotel={hotel} />
                  </div>
                </div>
              </div>

              <div className="md:order">
                <div
                  data-assistant-section="booking"
                  className="mt-5 w-full flex flex-col space-y-3 sticky top-[125px]"
                >
                  <BlocRight
                    data={hotel}
                    hotelPrice={hotelPriceObj}
                    minimumStay={minimumStay}
                  >
                    {!hotel?.hasToRequest && (
                      <SectionCard>
                        <Button
                          variant="primary"
                          tabIndex="2"
                          block
                          disabledRoles={['to_user']}
                          disabledRolesMessage={t('requestConnectionNoRight')}
                          onClick={handleRequest}
                        >
                          {t('requestConnection')}
                        </Button>
                      </SectionCard>
                    )}

                    {!hasFilter && !source && (
                      <SectionCard>
                        <Button
                          variant="primary"
                          tabIndex="2"
                          block
                          onClick={() => setShowGetAvailabilityModal(true)}
                        >
                          {t('getAvailabilityModal.title')}
                        </Button>
                      </SectionCard>
                    )}

                    {isSearchInProgress && (
                      <SectionCard>
                        <div className="flex flex-col space-y-3">
                          <KoobPlaceholder className="w-2/3 mx-auto h-6 rounded" />
                          <KoobPlaceholder className="w-full px-2 h-10 rounded" />
                          <KoobPlaceholder className="w-full h-10 rounded" />
                        </div>
                      </SectionCard>
                    )}

                    {noAvailabilityAfterSearch && (
                      <SectionCard>
                        <div className="flex flex-col space-y-3">
                          <div className="text-gray-500 text-sm text-center">
                            {t('unavailable')}
                          </div>

                          <Button
                            variant="secondary"
                            onClick={() => setShowGetAvailabilityModal(true)}
                          >
                            {t('getAvailabilityModal.editGuestsDates')}
                          </Button>
                        </div>
                      </SectionCard>
                    )}

                    {hotel?.canBeBooked &&
                      hasFilter &&
                      !noAvailabilityAfterSearch &&
                      !isSearchInProgress && (
                        <SectionCard>
                          <div className="flex flex-col space-y-3">
                            {(hotelPriceObj && hotelPriceObj.price) ||
                            canDisplay ? (
                              <>
                                <BookingSummaryCard
                                  title={hotel?.displayName}
                                  startAt={filters?.startAt}
                                  endAt={filters?.endAt}
                                  pax={totalPax}
                                />

                                {hotelPriceObj?.price ? (
                                  <>
                                    {hotelPriceObj?.appliedPromotions?.length >
                                      0 && (
                                      <div className="flex justify-center mb-1">
                                        <PromotionTag
                                          name={t('misc:promotionIncluded')}
                                          kind="basic_deal"
                                        />
                                      </div>
                                    )}

                                    <div className="flex justify-center">
                                      <PromotionIncludedPrice
                                        priceWithoutPromo={formatCurrency({
                                          amount:
                                            hotelPriceObj?.priceWithoutPromo,
                                          currency: hotelPriceObj?.currency,
                                        })}
                                        promotions={
                                          hotelPriceObj?.appliedPromotions
                                        }
                                      />
                                    </div>

                                    <div className="flex justify-center items-center space-x-3">
                                      <div className="text-gray-500 text-sm">
                                        {t('from')}
                                      </div>

                                      <div className="text-3xl text-orange-600 font-medium">
                                        {formatCurrency({
                                          amount: hotelPriceObj?.price,
                                          currency: hotelPriceObj?.currency,
                                        })}
                                      </div>

                                      <div className="text-center text-gray-500 text-sm">
                                        {t('perNight')}
                                      </div>
                                    </div>

                                    <div className="text-xl text-center font-medium text-gray-800">
                                      {formatCurrency({
                                        amount:
                                          hotelPriceObj?.price / totalPax ?? 1,
                                        currency: hotelPriceObj?.currency,
                                      })}{' '}
                                      / PAX
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-gray-500 text-sm">
                                    {t('unavailable')}
                                  </div>
                                )}

                                {hasFilter && nbNights < minimumStay ? (
                                  <div className="flex flex-col space-y-1">
                                    <div className="text-center text-red-500 font-medium bg-red-100 rounded py-2">
                                      {t('minimumStay', { count: minimumStay })}
                                    </div>

                                    <Button
                                      variant="primary"
                                      type="button"
                                      tabIndex="2"
                                      block
                                      onClick={() =>
                                        setShowRoomCompositionModal(true)
                                      }
                                    >
                                      {t('booking:editBookingPeriod')}
                                    </Button>
                                  </div>
                                ) : (
                                  <>
                                    <Button
                                      variant="primary"
                                      tabIndex="2"
                                      block
                                      onClick={handleBookingStart}
                                      disabled={!hotelPriceObj?.price}
                                      testId="book-now"
                                    >
                                      {t('booking:newBooking')}
                                    </Button>

                                    <KoobButton
                                      variant="outline"
                                      block
                                      onClick={handleAddToBasket}
                                      disabled={
                                        !basketAvailable ||
                                        !hotelPriceObj?.price
                                      }
                                    >
                                      {t('booking:addToBasket')}
                                    </KoobButton>
                                  </>
                                )}
                              </>
                            ) : (
                              <>
                                <KoobPlaceholder className="w-2/3 mx-auto h-6 rounded" />

                                <KoobPlaceholder className="w-full px-2 h-10 rounded" />

                                <KoobPlaceholder className="w-full h-10 rounded" />
                              </>
                            )}

                            <Button
                              variant="secondary"
                              onClick={() => setShowGetAvailabilityModal(true)}
                            >
                              {t('getAvailabilityModal.editGuestsDates')}
                            </Button>
                          </div>
                        </SectionCard>
                      )}
                  </BlocRight>
                </div>
              </div>
            </div>
          </div>
        )}

        <HotelGetAvailabilityModal
          open={showGetAvailabilityModal}
          setOpen={setShowGetAvailabilityModal}
          initialRooms={filters?.rooms ?? null}
          initialCheckIn={
            filters?.startAt ? parseDateOnly(filters.startAt) : null
          }
          initialCheckOut={filters?.endAt ? parseDateOnly(filters.endAt) : null}
          onSubmit={searchAvailability}
        />

        {showRoomCompositionModal && (
          <BookingRoomsCompositionModal
            multiselect
            fillChildrenBirthdatesFromFolder
            initialValue={filters?.rooms}
            onRequestClose={() => {
              setShowRoomCompositionModal(false);
              setBasketAvailable(false);
            }}
            onSave={rooms => {
              updateFilterValue('rooms', rooms);
              setShowRoomCompositionModal(false);
              setBasketAvailable(false);
              reloadPrice();
            }}
            hasNationalityField
            hasPeriodField
          />
        )}
      </KoobContainer>
    </>
  );
}
